// The positional layer's bookkeeping (suggestion "positional voice pool with virtual loops and nearest-N handoff";
// audio cards 6 and 15). Pure logic, no Web Audio: the engine asks it which voice a one-shot gets and which loop
// emitters get a live graph, and a test drives it with numbers.
//
// - One-shots: a fixed pool (10 to 24 voices). A free voice (its sound ended) is taken first; otherwise the lowest
//   priority, then the farthest, then the soonest-ending voice whose priority is at or under the new sound's is stolen
//   (the engine fades it out over 15 ms). Nothing free and nothing stealable: the sound is culled. Accounting is by
//   the scheduled end time, never by "now", so an offline render (where time jumps) can't steal every voice.
// - Loops: emitters always exist as handles; only the nearest `max` within their range get a graph. A built emitter
//   is released past range + `hysteresis` m. At the cap, an unbuilt emitter in range takes the farthest built one's
//   graph only when it is under `handoff` (55 %) of that one's distance, so two near-equal sources don't ping-pong.

/** What a one-shot asks the pool for. */
export interface VoiceRequest {
  priority: number;
  /** Metres from the listener (0 for a sound on the listener). */
  distance: number;
  /** When it will end (context seconds). */
  end: number;
}

/** A voice slot. */
export interface VoiceSlot {
  priority: number;
  distance: number;
  end: number;
  /** Increments each time the slot is given out (the engine's handle to stop the right sound). */
  serial: number;
}

/** The pool's answer: a slot (and whether it was stolen), or null when the sound is culled. */
export type VoiceGrant = { index: number; stolen: boolean; serial: number } | null;

/** A fixed pool of one-shot voices. */
export class VoicePool {
  readonly slots: VoiceSlot[];
  /** Counts since creation. */
  readonly counts = { granted: 0, stolen: 0, culled: 0, peakBusy: 0 };
  constructor(size: number) {
    const n = Math.max(1, Math.min(64, Math.round(size)));
    this.slots = Array.from({ length: n }, () => ({ priority: 0, distance: 0, end: -Infinity, serial: 0 }));
  }
  /** Voices still sounding at `now`. */
  busy(now: number): number {
    let b = 0;
    for (const s of this.slots) if (s.end > now) b++;
    return b;
  }
  /** Give a voice to a sound starting at `now`, stealing one if it must. */
  acquire(now: number, r: VoiceRequest): VoiceGrant {
    let pick = -1;
    let stolen = false;
    for (let i = 0; i < this.slots.length; i++) if (this.slots[i]!.end <= now) {
      pick = i;
      break;
    }
    if (pick < 0) {
      let best: VoiceSlot | null = null;
      for (let i = 0; i < this.slots.length; i++) {
        const s = this.slots[i]!;
        if (s.priority > r.priority) continue;
        // a steal must be worth it: a lower priority, or as important but farther than the new sound
        if (s.priority === r.priority && s.distance <= r.distance) continue;
        if (!best || s.priority < best.priority || (s.priority === best.priority && (s.distance > best.distance || (s.distance === best.distance && s.end < best.end)))) {
          best = s;
          pick = i;
        }
      }
      if (pick < 0) {
        this.counts.culled++;
        return null;
      }
      stolen = true;
      this.counts.stolen++;
    }
    const s = this.slots[pick]!;
    s.priority = r.priority;
    s.distance = r.distance;
    s.end = r.end;
    s.serial++;
    this.counts.granted++;
    this.counts.peakBusy = Math.max(this.counts.peakBusy, this.busy(now));
    return { index: pick, stolen, serial: s.serial };
  }
  /** Free a slot early (its sound was stopped). */
  release(index: number, serial: number): void {
    const s = this.slots[index];
    if (s && s.serial === serial) s.end = -Infinity;
  }
}

/** A loop emitter as the field sees it. */
export interface LoopEmitter {
  id: string;
  x: number;
  y: number;
  z: number;
  /** Audible within this distance (m). */
  range: number;
}

/** Options for the loop field. */
export interface LoopFieldOptions {
  /** Most emitters with a live graph at once (DYSTOPIA's 16). */
  max: number;
  /** Released past range + this (m). */
  hysteresis?: number;
  /** A candidate takes a built emitter's graph only under this fraction of its distance. */
  handoff?: number;
}

/** What one update changed. */
export interface LoopChanges {
  build: string[];
  release: string[];
}

/** Virtual loop emitters with nearest-N handoff. */
export class LoopField {
  private readonly emitters = new Map<string, LoopEmitter>();
  private readonly built = new Set<string>();
  readonly counts = { builds: 0, releases: 0, handoffs: 0, peakBuilt: 0 };
  constructor(private readonly o: LoopFieldOptions) {}
  /** Add or move an emitter. */
  set(e: LoopEmitter): void {
    this.emitters.set(e.id, { ...e });
  }
  remove(id: string): boolean {
    this.emitters.delete(id);
    return this.built.delete(id);
  }
  isBuilt(id: string): boolean {
    return this.built.has(id);
  }
  builtIds(): string[] {
    return [...this.built];
  }
  size(): number {
    return this.emitters.size;
  }
  /** Re-decide which emitters get a graph for a listener at (x, y, z). */
  update(x: number, y: number, z: number): LoopChanges {
    const hyst = this.o.hysteresis ?? 10;
    const ratio = this.o.handoff ?? 0.55;
    const d = new Map<string, number>();
    for (const e of this.emitters.values()) d.set(e.id, Math.hypot(e.x - x, e.y - y, e.z - z));
    const out: LoopChanges = { build: [], release: [] };
    // release what is past range + hysteresis
    for (const id of [...this.built]) {
      const e = this.emitters.get(id);
      if (!e || d.get(id)! > e.range + hyst) {
        this.built.delete(id);
        out.release.push(id);
      }
    }
    const candidates = [...this.emitters.values()].filter((e) => !this.built.has(e.id) && d.get(e.id)! < e.range).sort((a, b) => d.get(a.id)! - d.get(b.id)!);
    for (const c of candidates) {
      if (this.built.size < this.o.max) {
        this.built.add(c.id);
        out.build.push(c.id);
        continue;
      }
      // at the cap: hand the farthest built graph over only to a clearly nearer candidate
      let far: string | null = null;
      for (const id of this.built) if (far === null || d.get(id)! > d.get(far)!) far = id;
      if (far !== null && d.get(c.id)! < ratio * d.get(far)!) {
        this.built.delete(far);
        out.release.push(far);
        this.built.add(c.id);
        out.build.push(c.id);
        this.counts.handoffs++;
      }
    }
    this.counts.builds += out.build.length;
    this.counts.releases += out.release.length;
    this.counts.peakBuilt = Math.max(this.counts.peakBuilt, this.built.size);
    return out;
  }
}
